import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  getOrderDiscountList, getOrderDiscount, createOrderDiscount, updateOrderDiscount, deleteOrderDiscount,
} from '../../api/orderDiscount';
import { getStoreList } from '../../api/store';
import { formatDateTime } from '../../utils/format';

const DISCOUNT_TYPE_LABEL = { RATE: '정률(%)', AMOUNT: '정액(원)' };

function formatDiscountValue(discountType, discountValue) {
  if (discountValue == null) return '-';
  return discountType === 'RATE' ? `${discountValue}%` : `${Number(discountValue).toLocaleString()}원`;
}

function getPeriodStatus(discount) {
  const now = new Date();
  const start = new Date(discount.startDate);
  const end = new Date(discount.endDate);
  if (now < start) return { label: '적용예정', color: 'yellow' };
  if (now > end) return { label: '종료', color: 'gray' };
  return { label: '적용중', color: 'green' };
}

/**
 * 장바구니 주문금액 할인 관리 화면.
 * 최소주문금액 이상 결제 시 정률/정액으로 할인하고, 적용 기간과 전체매장/선택매장
 * 범위를 등록할 수 있다. 같은 기간에 여러 금액 구간(예: 5만원 5%, 10만원 10%)이
 * 겹쳐도 등록을 막지 않는다 - 체크아웃 시 가장 유리한 구간이 자동으로 선택된다.
 */
export default function OrderDiscountManagePage() {
  const { auth } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getOrderDiscountList();
      setList(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '할인 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleCreate = async (form) => {
    try {
      await createOrderDiscount({ ...form, createdBy: auth.adminName });
      setCreating(false);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '할인 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form) => {
    try {
      await updateOrderDiscount(editing.discountId, { ...form, updatedBy: auth.adminName });
      setEditing(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '할인 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrderDiscount(deleteTarget.discountId);
      setDeleteTarget(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '할인 삭제에 실패했습니다.');
    }
  };

  return (
    <AdminLayout title="주문할인관리" menuUrl="/admin/order-discount">
      <div className="page-header">
        <div>
          <h1>주문할인관리</h1>
          <div className="subtitle">장바구니 주문금액이 일정 금액 이상이면 할인을 적용합니다. 기간별, 전체매장/선택매장 단위로 등록할 수 있습니다.</div>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 할인 등록</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{list.length}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>할인명</th><th>최소주문금액</th><th>할인</th><th>상한액</th><th>매장범위</th>
                <th>적용기간</th><th>상태</th><th>사용</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={9} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && list.length === 0 && <tr><td colSpan={9} className="empty-row">등록된 할인이 없습니다.</td></tr>}
              {!loading && !error && list.map((d) => {
                const status = getPeriodStatus(d);
                return (
                  <tr key={d.discountId}>
                    <td>{d.discountName}</td>
                    <td>{Number(d.minOrderAmount).toLocaleString()}원 이상</td>
                    <td>{DISCOUNT_TYPE_LABEL[d.discountType] || d.discountType} {formatDiscountValue(d.discountType, d.discountValue)}</td>
                    <td>{d.maxDiscountAmount ? `${Number(d.maxDiscountAmount).toLocaleString()}원` : '-'}</td>
                    <td>{d.storeScope === 'ALL' ? '전체매장' : `선택매장 (${(d.storeIds || []).length}곳)`}</td>
                    <td className="text-secondary" style={{ fontSize: 12.5 }}>{formatDateTime(d.startDate)} ~ {formatDateTime(d.endDate)}</td>
                    <td><span className={`badge badge-${status.color}`}>{status.label}</span></td>
                    <td><StatusBadge value={d.useYn} label={d.useYn === 'Y' ? '사용' : '중지'} /></td>
                    <td className="actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(d)}>수정</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(d)}>삭제</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <OrderDiscountFormModal onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <OrderDiscountFormModal discountId={editing.discountId} onClose={() => setEditing(null)} onSave={handleUpdate} />}
      {deleteTarget && (
        <ConfirmDialog title="주문할인 삭제" message={`'${deleteTarget.discountName}' 할인을 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </AdminLayout>
  );
}

/** datetime-local <input> 은 "YYYY-MM-DDTHH:mm" 을 쓰므로, 서버가 내려주는 값에서 초 이하를 잘라서 맞춘다. */
function toDatetimeLocal(value) {
  if (!value) return '';
  return String(value).slice(0, 16);
}

function OrderDiscountFormModal({ discountId, onClose, onSave }) {
  const isNew = !discountId;
  const [form, setForm] = useState({
    discountName: '',
    minOrderAmount: '',
    discountType: 'RATE',
    discountValue: '',
    maxDiscountAmount: '',
    storeScope: 'ALL',
    storeIds: [],
    startDate: '',
    endDate: '',
    useYn: 'Y',
  });
  const [stores, setStores] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getStoreList().then((res) => setStores(res.data || [])).catch(() => setStores([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    getOrderDiscount(discountId)
      .then((res) => {
        if (cancelled || !res.data) return;
        const d = res.data;
        setForm({
          discountName: d.discountName || '',
          minOrderAmount: d.minOrderAmount ?? '',
          discountType: d.discountType || 'RATE',
          discountValue: d.discountValue ?? '',
          maxDiscountAmount: d.maxDiscountAmount ?? '',
          storeScope: d.storeScope || 'ALL',
          storeIds: d.storeIds || [],
          startDate: toDatetimeLocal(d.startDate),
          endDate: toDatetimeLocal(d.endDate),
          useYn: d.useYn || 'Y',
        });
      })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [discountId, isNew]);

  const toggleStore = (storeId) => {
    setForm((f) => {
      const has = f.storeIds.includes(storeId);
      return { ...f, storeIds: has ? f.storeIds.filter((id) => id !== storeId) : [...f.storeIds, storeId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.discountName.trim() || !form.minOrderAmount || !form.discountValue || !form.startDate || !form.endDate) {
      window.alert('할인명, 최소주문금액, 할인값, 적용기간을 모두 입력하세요.');
      return;
    }
    if (form.storeScope === 'SELECTED' && form.storeIds.length === 0) {
      window.alert('선택매장 범위는 매장을 1개 이상 선택해야 합니다.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        discountName: form.discountName.trim(),
        minOrderAmount: Number(form.minOrderAmount),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        maxDiscountAmount: form.discountType === 'RATE' && form.maxDiscountAmount !== '' ? Number(form.maxDiscountAmount) : null,
        storeScope: form.storeScope,
        storeIds: form.storeScope === 'SELECTED' ? form.storeIds : [],
        // datetime-local 값은 "YYYY-MM-DDTHH:mm" 형식이라 서버가 요구하는 초단위까지 맞춰서 보낸다.
        startDate: `${form.startDate}:00`,
        endDate: `${form.endDate}:00`,
        useYn: form.useYn,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isNew ? '주문할인 등록' : '주문할인 수정'} onClose={onClose} width={560}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || loadingDetail}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      {loadingDetail ? (
        <div className="empty-row">불러오는 중...</div>
      ) : (
        <form onSubmit={submit}>
          <div className="field">
            <label>할인명</label>
            <input className="input" value={form.discountName} onChange={(e) => setForm({ ...form, discountName: e.target.value })} required placeholder="예: 여름맞이 5만원 이상 할인" />
          </div>

          <div className="field">
            <label>최소 주문금액 (원 이상)</label>
            <input className="input" type="number" min="1" value={form.minOrderAmount} onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value })} required />
          </div>

          <div className="field-row">
            <div className="field">
              <label>할인방식</label>
              <select className="select" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value, maxDiscountAmount: '' })}>
                <option value="RATE">정률(%)</option>
                <option value="AMOUNT">정액(원)</option>
              </select>
            </div>
            <div className="field">
              <label>{form.discountType === 'RATE' ? '할인율 (%)' : '할인금액 (원)'}</label>
              <input className="input" type="number" min="0" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} required />
            </div>
          </div>

          {form.discountType === 'RATE' && (
            <div className="field">
              <label>할인 상한액 (선택, 원)</label>
              <input className="input" type="number" min="0" value={form.maxDiscountAmount} onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })} placeholder="비워두면 상한 없음" />
            </div>
          )}

          <div className="field-row">
            <div className="field">
              <label>적용 시작일시</label>
              <input className="input" type="datetime-local" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
            </div>
            <div className="field">
              <label>적용 종료일시</label>
              <input className="input" type="datetime-local" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
            </div>
          </div>

          <div className="field">
            <label>매장범위</label>
            <div className="field-row" style={{ gap: 16 }}>
              <label className="checkbox-row" style={{ margin: 0 }}>
                <input type="radio" name="storeScope" value="ALL" checked={form.storeScope === 'ALL'} onChange={() => setForm({ ...form, storeScope: 'ALL' })} />
                <span>전체매장</span>
              </label>
              <label className="checkbox-row" style={{ margin: 0 }}>
                <input type="radio" name="storeScope" value="SELECTED" checked={form.storeScope === 'SELECTED'} onChange={() => setForm({ ...form, storeScope: 'SELECTED' })} />
                <span>선택매장</span>
              </label>
            </div>
          </div>

          {form.storeScope === 'SELECTED' && (
            <div className="field">
              <label>적용 매장 선택</label>
              {stores.length === 0 && <div className="subtitle">등록된 매장이 없습니다. 먼저 매장관리에서 매장을 등록하세요.</div>}
              {stores.length > 0 && (
                <div className="checkbox-list" style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4, padding: 8 }}>
                  {stores.map((s) => (
                    <div key={s.storeId} className="field checkbox-row" style={{ marginBottom: 4 }}>
                      <input
                        type="checkbox"
                        id={`order-discount-store-${s.storeId}`}
                        checked={form.storeIds.includes(s.storeId)}
                        onChange={() => toggleStore(s.storeId)}
                      />
                      <label htmlFor={`order-discount-store-${s.storeId}`} style={{ margin: 0 }}>{s.storeName}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="field checkbox-row">
            <input type="checkbox" id="orderDiscountUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
            <label htmlFor="orderDiscountUseYn" style={{ margin: 0 }}>사용</label>
          </div>
        </form>
      )}
    </Modal>
  );
}
